import React, { useState } from 'react';
import { Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { LuCheck, LuChevronDown, LuGlobe, LuUser } from 'react-icons/lu';
import vanImg from '../../../assets/images/sahari-van.webp';
import Icon from '../../../components/ui/Icon';
import { ScrollText, Reveal } from '../../../components/motion';
import { phoneProblem } from '../../../data/dialCodes';
import Eyebrow from '../../../components/ui/Eyebrow';
import styles from './FreeQuoteSection.module.css';

/** Customers book from inside Saudi Arabia, so the number is always +966. */
const SAUDI_CODE = '+966';

/** The countries Sahari ships to — names match data/dialCodes.ts. */
const DESTINATIONS = [
  'India',
  'Pakistan',
  'Bangladesh',
  'Nepal',
  'Sri Lanka',
  'Philippines',
  'Malaysia',
  'Singapore',
  'Brunei',
  'United Kingdom',
];

/** What Book Shipment picks up from router state to prefill its form. */
export type QuotePrefill = { name: string; phoneCode: string; phone: string; country: string };

/**
 * Free Quote — a red banner with one-line form: name, Saudi mobile and where
 * it's going. "Get my quote" opens Book Shipment with those already filled in
 * (passed in router state, never in the URL).
 */
const FreeQuoteSection: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [errors, setErrors] = useState<{ name?: string; phone?: string }>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!name.trim()) next.name = 'Enter your name.';
    const problem = phoneProblem(SAUDI_CODE, phone);
    if (problem) next.phone = problem;
    setErrors(next);
    if (next.name || next.phone) return;
    const quote: QuotePrefill = { name: name.trim(), phoneCode: SAUDI_CODE, phone: phone.trim(), country };
    navigate('/book-shipment', { state: { quote } });
  };

  return (
    <section className={styles.freeQuote}>
      <Container fluid="xl" className={styles.inner}>
        <Reveal className={styles.banner}>
          <div className={styles.copy}>
            <Eyebrow light>Free quote</Eyebrow>
            <ScrollText as="h2" className={styles.title}>
              Tell us where it's going. We'll call with a price.
            </ScrollText>
            <p className={styles.subtitle}>
              Three details and you're done — a Sahari team member calls you back with the price and a pickup time.
            </p>
          </div>

          <form className={styles.bar} onSubmit={handleSubmit} noValidate>
            <div className={styles.van} aria-hidden="true">
              <img src={vanImg} alt="" />
            </div>

            <div className={styles.field}>
              <label className={`${styles.box} ${errors.name ? styles.boxBad : ''}`}>
                <Icon icon={LuUser} className={styles.lead} />
                <input
                  type="text"
                  autoComplete="name"
                  placeholder="Your name"
                  aria-label="Your name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (errors.name) setErrors((x) => ({ ...x, name: undefined }));
                  }}
                />
              </label>
              {errors.name && <span className={styles.error}>{errors.name}</span>}
            </div>

            <div className={styles.field}>
              <label className={`${styles.box} ${errors.phone ? styles.boxBad : ''}`}>
                <span className={styles.code}>🇸🇦 {SAUDI_CODE}</span>
                <input
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  placeholder="Mobile number"
                  aria-label="Mobile number"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value.replace(/[^\d\s]/g, ''));
                    if (errors.phone) setErrors((x) => ({ ...x, phone: undefined }));
                  }}
                />
              </label>
              {errors.phone && <span className={styles.error}>{errors.phone}</span>}
            </div>

            <div className={styles.field}>
              <label className={styles.box}>
                <Icon icon={LuGlobe} className={styles.lead} />
                <select
                  aria-label="Sending to"
                  className={country ? '' : styles.placeholder}
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                >
                  <option value="">Sending to</option>
                  {DESTINATIONS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <Icon icon={LuChevronDown} className={styles.caret} />
              </label>
            </div>

            <button type="submit" className={styles.go}>
              Get my quote <span aria-hidden="true">→</span>
            </button>
          </form>

          <ul className={styles.ticks}>
            {['Free, no obligation', 'We collect from your door', '10 countries'].map((t) => (
              <li key={t}>
                <Icon icon={LuCheck} className={styles.tick} />
                {t}
              </li>
            ))}
          </ul>
        </Reveal>
      </Container>
    </section>
  );
};

export default FreeQuoteSection;
